// Tenantra Inc. - Lease calendar: lease end dates, rent increase dates and provincial notice deadlines
import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api, { apiHelpers } from '@/services/api';
import { useUrlParam } from '@/services/useUrlParam';
import { DetailSheet, Section, Field, PrimaryAction, fmtDate } from '@/components/dashboard/DetailPanel';
import { TenantPanel } from '@/components/dashboard/TenantPanel';

const CATS = [['lease', 'Lease ends'], ['increase', 'Rent increases'], ['notice', 'Notice deadlines']];
const TONE = { lease: 'bg-slate-100 text-slate-700', increase: 'bg-teal-50 text-teal-800', notice: 'bg-amber-50 text-amber-800' };
const DOT = { lease: 'bg-slate-400', increase: 'bg-teal-500', notice: 'bg-amber-500' };
const pad = (n) => String(n).padStart(2, '0');
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parse = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d || 1); };
const addDays = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const thisMonth = () => iso(new Date()).slice(0, 7);
const monthName = (m) => parse(m).toLocaleDateString('en-CA', { month: 'long', year: 'numeric' });
const shift = (m, n) => { const d = parse(m); return iso(new Date(d.getFullYear(), d.getMonth() + n, 1)).slice(0, 7); };
const useMobile = () => { const [m, setM] = useState(() => window.matchMedia('(max-width: 639px)').matches); useEffect(() => { const q = window.matchMedia('(max-width: 639px)'); const h = (e) => setM(e.matches); q.addEventListener('change', h); return () => q.removeEventListener('change', h); }, []); return m; };

function range(view, month) {
  if (view === 'upcoming') { const t = new Date(); return [iso(t), iso(addDays(t, 365))]; }
  const first = parse(month); const start = addDays(first, -first.getDay());
  const weeks = Math.ceil((first.getDay() + new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate()) / 7);
  return [iso(start), iso(addDays(start, weeks * 7 - 1))];
}

const EventPill = ({ e, onOpen }) => (
  <button onClick={() => onOpen([e])} className={`w-full text-left truncate rounded px-1.5 py-0.5 text-[11px] transition-opacity hover:opacity-80 ${TONE[e.category]}`} title={`${e.title}: ${e.tenant_name}`} data-testid={`cal-event-${e.id}`}>
    {e.title.replace('Last day to give rent increase notice', 'Notice deadline').replace('Rent increase can start', 'Increase allowed')} · {e.tenant_name.split(' ')[0]}
  </button>
);

function MonthGrid({ month, events, onOpen }) {
  const [start, end] = range('month', month);
  const days = Array.from({ length: Math.round((parse(end) - parse(start)) / 864e5) + 1 }, (_, i) => addDays(parse(start), i));
  const byDay = useMemo(() => events.reduce((m, e) => ({ ...m, [e.date]: [...(m[e.date] || []), e] }), {}), [events]);
  const today = iso(new Date());
  return (
    <div data-testid="cal-grid">
      <div className="grid grid-cols-7 border-b border-slate-100">{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} className="px-2 py-2 text-[11px] text-slate-400">{d}</div>)}</div>
      <div className="grid grid-cols-7">
        {days.map(d => { const k = iso(d); const list = byDay[k] || []; const out = k.slice(0, 7) !== month; return (
          <div key={k} className={`min-h-[104px] border-b border-r border-slate-100 p-1.5 space-y-1 ${out ? 'bg-slate-50/50' : ''}`} data-testid={`cal-day-${k}`}>
            <span className={`inline-flex w-6 h-6 items-center justify-center rounded-full text-xs ${k === today ? 'bg-teal-600 text-white font-medium' : out ? 'text-slate-300' : 'text-slate-600'}`}>{d.getDate()}</span>
            {list.slice(0, 3).map(e => <EventPill key={e.id} e={e} onOpen={onOpen} />)}
            {list.length > 3 && <button onClick={() => onOpen(list)} className="text-[11px] text-slate-500 hover:text-slate-800 px-1.5" data-testid={`cal-more-${k}`}>+{list.length - 3} more</button>}
          </div>
        ); })}
      </div>
    </div>
  );
}

function Agenda({ events, onOpen, grouped }) {
  const groups = useMemo(() => events.reduce((m, e) => { const g = grouped ? e.date.slice(0, 7) : 'all'; (m[g] = m[g] || []).push(e); return m; }, {}), [events, grouped]);
  return (
    <div data-testid="cal-agenda">
      {Object.entries(groups).map(([g, list]) => (
        <section key={g}>
          {grouped && <p className="px-5 pt-4 pb-1 text-[11px] font-semibold text-slate-500">{monthName(g)}</p>}
          <ul className="divide-y divide-slate-100">
            {list.map(e => { const d = parse(e.date); return (
              <li key={e.id}>
                <button onClick={() => onOpen([e])} className="w-full px-5 py-3 flex items-center gap-4 text-left hover:bg-slate-50/70 transition-colors" data-testid={`cal-row-${e.id}`}>
                  <span className="w-10 text-center flex-shrink-0"><span className="block text-lg font-semibold text-slate-800 leading-none">{d.getDate()}</span><span className="text-[11px] text-slate-400">{d.toLocaleDateString('en-CA', { weekday: 'short' })}</span></span>
                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${DOT[e.category]}`} />
                  <span className="flex-1 min-w-0"><span className="block text-sm text-slate-800 truncate">{e.title}</span><span className="block text-xs text-slate-500 truncate">{e.tenant_name} · {e.address}</span></span>
                </button>
              </li>
            ); })}
          </ul>
        </section>
      ))}
    </div>
  );
}

function EventSheet({ list, onClose, onTenant, onPick }) {
  const e = list?.length === 1 ? list[0] : null;
  return (
    <DetailSheet open={!!list} onClose={onClose} crumb="Calendar" testId="cal-sheet"
      title={e ? e.title : list ? fmtDate(list[0].date) : ''}
      meta={e && <><span className="text-slate-600">{fmtDate(e.date)}</span><span className={`px-2 py-0.5 rounded-full ${TONE[e.category]}`}>{CATS.find(c => c[0] === e.category)[1]}</span></>}
      footer={e && <PrimaryAction onClick={() => onTenant(e.tenant_id)} testId="cal-open-tenant">Open tenant</PrimaryAction>}>
      {e ? <>
        <Section title="Details">
          <p className="text-sm text-slate-700 mb-4" data-testid="cal-sheet-detail">{e.detail}</p>
          <dl className="grid grid-cols-2 gap-4"><Field label="Tenant">{e.tenant_name}</Field><Field label="Property">{e.address}</Field></dl>
        </Section>
        {e.rule && <Section title={`${e.province} rules`}>
          <p className="text-sm text-slate-700" data-testid="cal-sheet-rule">{e.rule}</p>
          <a href={e.rule_url} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-teal-700 hover:text-teal-800" data-testid="cal-sheet-rule-link">Read the official rules <ExternalLink className="w-3 h-3" /></a>
        </Section>}
      </> : list && <ul className="divide-y divide-slate-100">{list.map(x => (
        <li key={x.id}><button onClick={() => onPick([x])} className="w-full px-6 py-3 flex items-center gap-3 text-left text-sm hover:bg-slate-50/70 transition-colors" data-testid={`cal-day-item-${x.id}`}><span className={`w-1.5 h-1.5 rounded-full ${DOT[x.category]}`} /><span className="flex-1 min-w-0"><span className="block text-slate-800">{x.title}</span><span className="block text-xs text-slate-500 truncate">{x.tenant_name} · {x.address}</span></span><ChevronRight className="w-4 h-4 text-slate-300" /></button></li>
      ))}</ul>}
    </DetailSheet>
  );
}

export default function CalendarPage() {
  const mobile = useMobile();
  const [view, setView] = useUrlParam('view', 'month');
  const [rawMonth, setMonth] = useUrlParam('month', thisMonth());
  const month = /^\d{4}-(0[1-9]|1[0-2])$/.test(rawMonth) ? rawMonth : thisMonth();
  const [hidden, setHidden] = useState([]);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [tenantId, setTenantId] = useState(null);
  const [start, end] = range(view, month);
  useEffect(() => {
    let live = true; setData(null);
    api.get('/landlord/calendar', { params: { start, end } }).then(r => live && setData(r.data.events)).catch(e => live && toast.error(apiHelpers.handleError(e)));
    return () => { live = false; };
  }, [start, end]);
  const inView = (data || []).filter(e => view === 'upcoming' || e.date.slice(0, 7) === month);
  const shown = inView.filter(e => !hidden.includes(e.category));
  const gridEvents = (data || []).filter(e => !hidden.includes(e.category));
  const count = (c) => inView.filter(e => e.category === c).length;
  return (
    <div className="space-y-4" data-testid="calendar-page">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-[15px] font-medium text-slate-800">Calendar</h1>
        <div className="inline-flex rounded-full bg-slate-100/80 p-0.5 text-xs" data-testid="cal-view-toggle">
          {[['month', 'Month'], ['upcoming', 'Next 12 months']].map(([k, l]) => <button key={k} onClick={() => setView(k)} className={`h-7 px-3 rounded-full transition-colors ${view === k ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500 hover:text-slate-800'}`} data-testid={`cal-view-${k}`}>{l}</button>)}
        </div>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg" data-testid="cal-card">
        <div className="flex flex-wrap items-center gap-2.5 px-5 py-3 border-b border-slate-100">
          {view === 'month' && <div className="mr-auto inline-flex items-center gap-1" data-testid="cal-month-picker">
            <button onClick={() => setMonth(shift(month, -1))} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" aria-label="Previous month" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-sm font-medium text-slate-800 w-36 text-center" data-testid="cal-month-label">{monthName(month)}</span>
            <button onClick={() => setMonth(shift(month, 1))} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" aria-label="Next month" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
            {month !== thisMonth() && <button onClick={() => setMonth(thisMonth())} className="ml-1 text-xs font-medium text-teal-700 hover:text-teal-800" data-testid="cal-today">Today</button>}
          </div>}
          <div className={`flex flex-wrap gap-1.5 ${view === 'upcoming' ? 'mr-auto' : ''}`} data-testid="cal-filters">
            {CATS.map(([k, l]) => { const on = !hidden.includes(k); return (
              <button key={k} onClick={() => setHidden(h => on ? [...h, k] : h.filter(x => x !== k))} className={`h-7 px-2.5 rounded-full text-xs inline-flex items-center gap-1.5 transition-colors ${on ? 'bg-slate-100 text-slate-700' : 'text-slate-400 line-through'}`} data-testid={`cal-filter-${k}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${DOT[k]}`} />{l}<span className="tabular-nums text-slate-400">{data ? count(k) : ''}</span>
              </button>
            ); })}
          </div>
        </div>
        {!data ? <div className="p-5 space-y-2">{[0, 1, 2].map(i => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}</div>
          : view === 'month' && !mobile ? <MonthGrid month={month} events={gridEvents} onOpen={setOpen} />
          : shown.length === 0 ? <p className="px-5 py-10 text-sm text-slate-500 text-center" data-testid="cal-empty">No lease dates {view === 'month' ? `in ${monthName(month)}` : 'in the next 12 months'}.</p>
          : <Agenda events={shown} onOpen={setOpen} grouped={view === 'upcoming'} />}
      </div>
      <p className="text-xs text-slate-500" data-testid="cal-footnote">Lease end dates come from each signed lease. Rent increase dates are counted from the move-in date. Dates are a guide based on provincial rules, so confirm with your province before giving notice.</p>
      <EventSheet list={open} onClose={() => setOpen(null)} onPick={setOpen} onTenant={(id) => { setOpen(null); setTenantId(id); }} />
      <TenantPanel tenantId={tenantId} onClose={() => setTenantId(null)} />
    </div>
  );
}
